import { useContext, useEffect, useState } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { ThemeProvider } from "@mui/system";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

import { APP_STATES, STREAM_TYPES } from "shared/enum";
import { AppContext } from "contexts/App";
import { DataContext, DataProvider } from "contexts/Data";
import { StreamContext, StreamProvider } from "contexts/Stream";
import HomeView from "views/Home";
import InitialLoading from "views/InitialLoading";
import { isDarkTheme } from "helpers/darkTheme";

import type { ThemeOptions } from "@mui/material/styles";

declare module "@mui/material/styles" {
  interface TypographyVariants {
    collapsible: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    collapsible?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    collapsible: true;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      retry: 3,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * DummyRouter is a component that acts as a router for the application.
 * It checks the appLoadingState from the AppContext and renders the appropriate view.
 * All initial global events (& their corresponding react state) are registered & managed here (for the three js scene container only).
 */
function DummyRouter() {
  const { appLoadingState, setAppLoadingState } = useContext(AppContext);
  const { loaded: configLoaded, loadError: configLoadError } = useContext(DataContext);
  const {
    checkSessionAvailability,
    setStreamingState,
    setTurntableState,
    loaded: streamDataLoaded,
    loadError: streamDataLoadError,
    setStreamViewType,
  } = useContext(StreamContext);
  const [loadingError, setLoadingError] = useState<boolean>(false);
  const [loadingProgress] = useState<number>(0);

  const handleLoadingClick = () => {
    checkSessionAvailability()
      .then((data) => {
        setTurntableState((prev) => ({
          ...prev,
          isTurntable: data?.status !== 200,
        }));

        setStreamingState((prev) => ({
          ...prev,
          isStreamingAvailable: data?.status === 200,
          callStreamingAvailableAPI: data?.status !== 200,
          isDontNotify: false,
        }));

        setStreamViewType(data?.status === 200 ? STREAM_TYPES.STREAMING : STREAM_TYPES.TURNTABLE);
      })
      .catch((err) => {
        console.log("err", err);
        setTurntableState((prev) => ({
          ...prev,
          isTurntable: true,
        }));

        setStreamingState((prev) => ({
          ...prev,
          isStreamingAvailable: false,
          callStreamingAvailableAPI: true,
          isDontNotify: false,
        }));

        setStreamViewType(STREAM_TYPES.TURNTABLE);
      });

    if (appLoadingState === APP_STATES.UNLOADED || loadingError) {
      try {
        setAppLoadingState(APP_STATES.LOADED);

        window.gtag("event", "loading", {
          event_category: "loading",
          event_label: "Initial load begin",
        });
      } catch (error) {
        console.error("Failed to load the scene!", error);
        setLoadingError(true);
      }
    }
  };

  if (appLoadingState === APP_STATES.UNLOADED || appLoadingState === APP_STATES.INITIAL_LOADING) {
    return (
      <InitialLoading
        error={loadingError}
        progress={loadingProgress}
        dataLoaded={configLoaded && streamDataLoaded}
        dataLoadError={configLoadError || streamDataLoadError}
        loadingState={appLoadingState}
        handleLoadingClick={handleLoadingClick}
      />
    );
  }

  return null;
}

function App() {
  const { themeMode, setThemeMode } = useContext(AppContext);

  const theme: ThemeOptions = {
    typography: {
      fontFamily: "Poppins",
      allVariants: {
        color: isDarkTheme(themeMode) ? "#ADB8C1" : "#000",
      },
      collapsible: {
        fontFamily: "Oxanium",
        color: isDarkTheme(themeMode) ? "#ADB8C1" : "#000",
        fontWeight: 400,
        fontSize: 20,
      },
    },
    palette: {
      mode: themeMode,
      primary: {
        main: isDarkTheme(themeMode) ? "#fff" : "#000",
      },
      secondary: {
        main: isDarkTheme(themeMode) ? "#ADB8C1" : "#000",
      },
      background: {
        default: isDarkTheme(themeMode) ? "#212A32" : "#fff",
        paper: isDarkTheme(themeMode) ? "#212A32" : "#fff",
      },
    },
    /**
     * Z-index values for different components (target sites header has a z-index of 999, so we need to make sure our components are below that)
     */
    zIndex: {
      drawer: 800,
      modal: 900,
      tooltip: 950,
    },
    /**
     * To prevent the useMediaQuery hook from rendering on the server (in simple words, to prevent rendering 2 times given we have only client side rendering)
     * @see https://mui.com/material-ui/react-use-media-query/
     */
    components: {
      MuiUseMediaQuery: {
        defaultProps: {
          noSsr: true,
        },
      },
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            collapsible: "h3",
          },
        },
      },
    },
  };

  useEffect(() => {
    function checkOsTheme({ matches }: { matches: boolean }) {
      // TODO: optimize light theme then enable light mode
      setThemeMode(matches ? "dark" : "dark");
    }

    window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", checkOsTheme);

    return () => {
      window.matchMedia("(prefers-color-scheme: dark)").removeEventListener("change", checkOsTheme);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ThemeProvider theme={createTheme(theme)}>
      <CssBaseline />

      <QueryClientProvider client={queryClient}>
        <Router>
          <div
            style={{
              height: "100%",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <DataProvider>
              <StreamProvider>
                <DummyRouter />
                <HomeView />
              </StreamProvider>
            </DataProvider>
          </div>
        </Router>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
